import { Button, Group, Image, Modal, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";

import type { ImageLayerEditQuote, ImageLayerEditRequest, Job } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import type { PickerLibrary } from "../../../platform/media/ImagePicker";
import { Price } from "../../../ui/Price";
import type { EditLanding } from "./edits";
import type { PictureLayer } from "./model";
import { useTransparency } from "./transparency";
import classes from "./layer-editor.module.css";

/** Live's prompt cap. */
const MOST = 4000;

const MODES = [
  { value: "replace", label: "Replace layer" },
  { value: "add", label: "Add as new layer" },
];

export interface EditLayerModalProps {
  library: PickerLibrary;
  /** The Layerize being edited. */
  layerizeJobId: string;
  layer: PictureLayer;
  /** The edit has started: where it is to land. */
  onStarted: (job: Job, landing: EditLanding) => void;
  onClose: () => void;
}

/**
 * Live's "Edit layer with AI" (spec G13b; `layer-edit-modal`): the layer's picture, "What should
 * change?", "Replace layer" or "Add as new layer", the price and "Edit layer". The price is for
 * the exact request the button sends, its removal included, so the button waits until the
 * layer's transparency is known and a prompt is written.
 */
export function EditLayerModal({ library, layerizeJobId, layer, onStarted, onClose }: EditLayerModalProps) {
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState<EditLanding["mode"]>("replace");
  const { transparent, checking } = useTransparency(layer.url);
  const written = prompt.trim() !== "";
  // Priced once the prompt is written, on an empty prompt (the price does not depend on it), so
  // typing does not price it again.
  const quote: ImageLayerEditQuote | undefined =
    checking || !written
      ? undefined
      : { type: "image.layer_edit", layerize_job_id: layerizeJobId, source: layer.ref, prompt: "", remove_background: transparent };
  const request: ImageLayerEditRequest | undefined = quote === undefined ? undefined : { ...quote, prompt: prompt.trim() };
  const job = usePricedStart({
    teamId: library.teamId,
    brandId: library.brandId,
    quote,
    request,
    onStarted: (started) => onStarted(started, { layer: layer.id, mode }),
  });
  return (
    <Modal
      opened
      onClose={onClose}
      centered
      title={
        <Stack gap={2}>
          <Text fw={600}>Edit layer with AI</Text>
          <Text size="sm" c="var(--cai-color-text-muted)">
            Describe the change. The edit is charged when it starts.
          </Text>
        </Stack>
      }
    >
      <Stack gap="md">
        <div className={classes.editPreview}>
          <Image src={layer.url} alt="" fit="contain" h="100%" />
        </div>
        <Textarea
          label="What should change?"
          placeholder="Make the bottle matte black"
          autosize
          minRows={3}
          maxRows={8}
          maxLength={MOST}
          data-autofocus
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        <SegmentedControl
          fullWidth
          aria-label="Where the edit lands"
          data={MODES}
          value={mode}
          onChange={(value) => setMode(value === "add" ? "add" : "replace")}
        />
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        <Group justify="space-between" wrap="nowrap">
          <Price priced={job.priced} pending={job.pricing && quote !== undefined} unpriced="Describe the change to see the price." />
          <Group gap="xs" wrap="nowrap">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={job.start.isPending} disabled={!job.canStart} onClick={job.startJob}>
              Edit layer
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
